import { Box } from "@mui/material";
import Wrapper from "components/LayoutFront/Wrapper";
import Block from "components/templatesComponents/Block";
import PageTitle from "components/templatesComponents/PageTitle";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef } from "react";
import { debounce } from "utils/commonUtils";

const attachWidgetContentToContainer = (element) => {
  const widgetIframeElement = document.getElementById("widgetIframe");

  if (widgetIframeElement && widgetIframeElement.parentElement !== element) {
    element.appendChild(widgetIframeElement);
  }
};

const Cyber = (props) => {
  const { page } = props;
  const { title, shortDescription, contents } = page;
  const { firstDynamicPart, secondDynamicPart } = contents;
  const containerRef = useRef(null);
  const cyberWidgetToken = process.env.cyber_widget_token || "";
  const cyberWidgetDomainName = process.env.cyber_widget_domain_name || "";

  const safeToken = useMemo(
    () => (typeof cyberWidgetToken === "string" ? cyberWidgetToken.replace(/"/g, "") : ""),
    [cyberWidgetToken],
  );
  const safeDomainName = useMemo(
    () => (typeof cyberWidgetDomainName === "string" ? cyberWidgetDomainName.replace(/"/g, "") : ""),
    [cyberWidgetDomainName],
  );

  const safeScriptNonce = useMemo(() => {
    if (typeof document !== "undefined") {
      const firstNoncedScript = document.querySelector("script[nonce]");
      const scriptNonce = firstNoncedScript?.nonce || firstNoncedScript?.getAttribute("nonce") || "";
      return typeof scriptNonce === "string" ? scriptNonce.replace(/"/g, "") : "";
    }
    return "";
  }, []);

  useEffect(() => {
    const containerElement = containerRef.current;

    if (!safeToken || !safeDomainName || !containerElement) {
      return undefined;
    }

    const widgetConfig = {
      token: safeToken,
      domainName: safeDomainName,
      mode: "page",
      target: containerElement,
    };

    const linkTag = document.createElement("link");
    linkTag.href = `https://${widgetConfig.domainName}/build-widget/style.css`;
    linkTag.rel = "stylesheet";
    if (safeScriptNonce) {
      linkTag.setAttribute("nonce", safeScriptNonce);
    }
    document.head.appendChild(linkTag);

    const styleScriptTag = document.createElement("script");
    styleScriptTag.src = `https://${widgetConfig.domainName}/build-widget/style.js`;
    styleScriptTag.type = "text/javascript";
    if (safeScriptNonce) {
      styleScriptTag.setAttribute("nonce", safeScriptNonce);
    }
    document.body.appendChild(styleScriptTag);

    const appScriptTag = document.createElement("script");
    appScriptTag.src = `https://${widgetConfig.domainName}/build-widget/app.js`;
    appScriptTag.type = "text/javascript";
    if (safeScriptNonce) {
      appScriptTag.setAttribute("nonce", safeScriptNonce);
    }

    appScriptTag.onload = () => {
      if (typeof window.loadWidget === "function") {
        window.loadWidget(widgetConfig);
        debounce(attachWidgetContentToContainer, 100)(containerElement);
      }
    };

    document.body.appendChild(appScriptTag);

    return () => {
      if (appScriptTag.parentNode) {
        appScriptTag.parentNode.removeChild(appScriptTag);
      }
      if (styleScriptTag.parentNode) {
        styleScriptTag.parentNode.removeChild(styleScriptTag);
      }
      if (linkTag.parentNode) {
        linkTag.parentNode.removeChild(linkTag);
      }
      containerElement.innerHTML = "";
    };
  }, [safeToken, safeDomainName, safeScriptNonce, containerRef]);

  return (
    <Wrapper>
      <Block>
        <PageTitle>{title}</PageTitle>
      </Block>
      {shortDescription && <Block>{shortDescription}</Block>}
      {firstDynamicPart}
      <Box ref={containerRef} id="cyber_widget" sx={{ width: "100%", height: 650 }} />
      {secondDynamicPart}
    </Wrapper>
  );
};

Cyber.propTypes = {
  page: PropTypes.shape().isRequired,
};

export default Cyber;
